import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { API_BASE_URL } from '../apiSetup/apiConfig';
import apiClient from '../stateManagers/services/axiosInterceptor';
import CustomPaginator from '../components/CustomPaginator';

interface LogEntry {
  timestamp: string;
  action: string;
  message: string;
}

interface BackupHistoryEntry {
  filename: string;
  sizeLabel: string;
  createdAt: string;
}

// NOTE: field names here follow the `bson` tags seen on models.BackupLog in
// the controller. If that struct has no explicit `json:"..."` tags, Go will
// instead marshal with capitalized field names (DatabaseName, Status, ...) —
// check the model and adjust these keys (and the accessors below) if so.
interface BackupLogRecord {
  _id?: string;
  database_name: string;
  operation: 'backup' | 'restore' | string;
  status: 'success' | 'failed' | 'in_progress' | string;
  trigger_type: string;
  triggered_by_name?: string;
  archive_file_name?: string;
  archive_size_mb?: number;
  duration_seconds?: number;
  error_message?: string;
  error_details?: string;
  created_at: string;
}

interface PaginationMeta {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

const formatBytes = (bytes: number): string => {
  if (bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
};

const formatMB = (mb?: number): string => {
  if (mb === undefined || mb === null) return '—';
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(2)} MB`;
};

const formatDuration = (seconds?: number): string => {
  if (seconds === undefined || seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
};

const formatDate = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
};

// Pulls the filename out of Content-Disposition: attachment; filename=xxx.tar.gz
const extractFilename = (contentDisposition: string | undefined, fallback: string): string => {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
};

const statusSeverity = (status: string): 'success' | 'danger' | 'info' | 'warning' => {
  switch (status) {
    case 'success':
      return 'success';
    case 'failed':
      return 'danger';
    case 'in_progress':
      return 'info';
    default:
      return 'warning';
  }
};

const operationSeverity = (operation: string): 'success' | 'warning' => {
  return operation === 'restore' ? 'warning' : 'success';
};

const ROWS_PER_PAGE = 10;

export default function DatabaseBackupComponent() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [history, setHistory] = useState<BackupHistoryEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useRef<Toast>(null);

  // ── Last backup status widget ──────────────────────────────────────────
  const [lastStatus, setLastStatus] = useState<BackupLogRecord | null>(null);
  const [lastStatusLoading, setLastStatusLoading] = useState<boolean>(true);

  // ── Backup history table (server-paginated via /api/backup/logs) ───────
  const [backupLogs, setBackupLogs] = useState<BackupLogRecord[]>([]);
  const [logsPage, setLogsPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(ROWS_PER_PAGE);
  const [pagination, setPagination] = useState<PaginationMeta>({
    current_page: 1,
    per_page: ROWS_PER_PAGE,
    total_items: 0,
    total_pages: 0,
  });
  const [tableLoading, setTableLoading] = useState<boolean>(true);

  // ── Restore ────────────────────────────────────────────────────────────
  const [restoring, setRestoring] = useState<boolean>(false);
  const [selectedRestoreFile, setSelectedRestoreFile] = useState<File | null>(null);
  const restoreFileInputRef = useRef<HTMLInputElement>(null);

  const addLog = (action: string, message: string) => {
    const timestamp = new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
    setLogs((prev) => [...prev, { timestamp, action, message }]);
  };

  // ── Fetch last backup status ────────────────────────────────────────────
  const fetchLastStatus = useCallback(async () => {
    setLastStatusLoading(true);
    try {
      const response = await apiClient.get(`api/backup/last-status`);
      setLastStatus(response.data?.data ?? null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to load last backup status.';
      addLog('Fetch Last Status', `Error: ${errorMessage}`);
    } finally {
      setLastStatusLoading(false);
    }
  }, []);

  // ── Fetch paginated backup logs ──────────────────────────────────────────
  const fetchBackupLogs = useCallback(async (page: number, limit: number) => {
    setTableLoading(true);
    try {
      const response = await apiClient.get(`api/backup/logs`, {
        params: { page, limit },
      });
      setBackupLogs(response.data?.data ?? []);
      if (response.data?.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to load backup history.';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: errorMessage, life: 4000 });
      addLog('Fetch Backup Logs', `Error: ${errorMessage}`);
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLastStatus();
  }, [fetchLastStatus]);

  useEffect(() => {
    fetchBackupLogs(logsPage, pageSize);
  }, [logsPage, pageSize, fetchBackupLogs]);

  const handleCreateAndDownloadBackup = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`api/backup/download`, {
        responseType: 'blob',
      });

      const fallbackName = `skoolpillar_backup_${Date.now()}.tar.gz`;
      const filename = extractFilename(response.headers['content-disposition'], fallbackName);
      const blob = new Blob([response.data], { type: 'application/gzip' });
      const sizeLabel = formatBytes(blob.size);

      // Trigger the browser download — axios/Gin won't do this automatically for a blob response
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setHistory((prev) => [
        { filename, sizeLabel, createdAt: new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }) },
        ...prev,
      ]);

      toast.current?.show({
        severity: 'success',
        summary: 'Backup Ready',
        detail: `${filename} (${sizeLabel}) downloaded successfully!`,
        life: 4000,
      });
      addLog('Create & Download Backup', `Success: ${filename} (${sizeLabel})`);

      // The download just wrote a new log row on the server — refresh both
      // the status widget and page 1 of the history table to reflect it.
      fetchLastStatus();
      setLogsPage(1);
      fetchBackupLogs(1, pageSize);
    } catch (error: any) {
      // Errors from a blob-typed request arrive as a Blob too, not JSON — read it out if present
      let errorMessage = 'Failed to create backup.';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMessage = parsed.message || errorMessage;
        } catch {
          // fall through to default message
        }
      } else {
        errorMessage = error.response?.data?.message || errorMessage;
      }

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 4000,
      });
      addLog('Create & Download Backup', `Error: ${errorMessage}`);

      // Even a failed attempt writes an "in_progress" -> "failed" log row.
      fetchLastStatus();
      setLogsPage(1);
      fetchBackupLogs(1, pageSize);
    } finally {
      setLoading(false);
    }
  };

  // ── Restore handlers ────────────────────────────────────────────────────

  const handleRestoreFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedRestoreFile(file);
  };

  const performRestore = async () => {
    if (!selectedRestoreFile) return;
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('backup_file', selectedRestoreFile);

      // NOTE: do NOT set Content-Type manually here — axios/the browser
      // must generate it (it includes the multipart boundary). Setting it
      // explicitly to 'multipart/form-data' strips the boundary and the
      // backend's multipart parser silently fails to read the file.
      const response = await apiClient.post(`api/backup/restore`, formData);

      const message = response.data?.message || 'Database restored successfully.';
      toast.current?.show({
        severity: 'success',
        summary: 'Restore Complete',
        detail: message,
        life: 4000,
      });
      addLog('Restore Backup', `Success: ${selectedRestoreFile.name}`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to restore backup.';
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 4000,
      });
      addLog('Restore Backup', `Error: ${errorMessage}`);
    } finally {
      setRestoring(false);
      setSelectedRestoreFile(null);
      if (restoreFileInputRef.current) restoreFileInputRef.current.value = '';
      fetchLastStatus();
      setLogsPage(1);
      fetchBackupLogs(1, pageSize);
    }
  };

  const handleRestoreClick = () => {
    if (!selectedRestoreFile) {
      restoreFileInputRef.current?.click();
      return;
    }
    confirmDialog({
      message: `This will overwrite the current database with the contents of "${selectedRestoreFile.name}". This cannot be undone. Continue?`,
      header: 'Confirm Database Restore',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: performRestore,
    });
  };

  // ── Table body templates (Students-list style) ──────────────────────────

  const operationTemplate = (row: BackupLogRecord) => (
    <Tag
      value={row.operation === 'restore' ? 'Restore' : 'Backup'}
      severity={operationSeverity(row.operation)}
      style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
    />
  );

  const statusTemplate = (row: BackupLogRecord) => (
    <div
      className={row.status === 'failed' && row.error_message ? 'backup-error-cell' : ''}
      data-pr-tooltip={row.status === 'failed' ? row.error_message : undefined}
    >
      <Tag
        value={row.status}
        severity={statusSeverity(row.status)}
        style={{ fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}
      />
    </div>
  );

  const databaseTemplate = (row: BackupLogRecord) => (
    <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 600 }}>{row.database_name || '—'}</div>
  );

  const triggerTypeTemplate = (row: BackupLogRecord) => (
    <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 500, textTransform: 'capitalize' }}>
      {row.trigger_type || '—'}
    </div>
  );

  const triggeredByTemplate = (row: BackupLogRecord) => (
    <div style={{ fontSize: 13, color: row.triggered_by_name ? '#1f2937' : '#9ca3af', fontWeight: 500 }}>
      {row.triggered_by_name || '—'}
    </div>
  );

  const fileTemplate = (row: BackupLogRecord) => (
    <div
      style={{
        fontSize: 13,
        color: row.archive_file_name ? '#1f2937' : '#9ca3af',
        fontWeight: 500,
        fontFamily: 'monospace',
        maxWidth: 220,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={row.archive_file_name || undefined}
    >
      {row.archive_file_name || '—'}
    </div>
  );

  const sizeTemplate = (row: BackupLogRecord) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{formatMB(row.archive_size_mb)}</div>
  );

  const durationTemplate = (row: BackupLogRecord) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{formatDuration(row.duration_seconds)}</div>
  );

  const createdTemplate = (row: BackupLogRecord) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{formatDate(row.created_at)}</div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <ConfirmDialog />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <style>{`
          @keyframes slideIn { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }
          .p-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
          .p-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 #4169E1; }
          .p-button-text:hover { background:#f3f4f6!important; }
          .p-datatable .p-datatable-thead > tr > th {
            background:#f9fafb!important;
            color:#6b7280!important;
            font-size:11px!important;
            font-weight:700!important;
            text-transform:uppercase!important;
            letter-spacing:.8px!important;
            padding:16px 12px!important;
            border-bottom:1px solid #e5e7eb!important;
          }
          .p-datatable .p-datatable-tbody > tr > td {
            padding:16px 12px!important;
            border-bottom:1px solid #f3f4f6!important;
          }
          .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
          .p-datatable .p-datatable-emptymessage td { padding:0!important; }
        `}</style>

        {/* ── Action cards grid ─────────────────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 20,
            marginBottom: 24,
          }}
        >
          {/* Backup Actions */}
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              padding: 24,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg,rgba(16,185,129,.12),rgba(5,150,105,.08))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className="pi pi-database" style={{ fontSize: 16, color: '#059669' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 2 }}>
                  Backup Actions
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                  Dump the full database and download as .tar.gz
                </div>
              </div>
            </div>
            <Button
              label={loading ? 'Creating Backup…' : 'Create & Download Backup'}
              icon="pi pi-cloud-download"
              onClick={handleCreateAndDownloadBackup}
              loading={loading}
              style={{
                background: 'linear-gradient(135deg,#10b981,#059669)',
                border: 'none',
                borderRadius: 12,
                padding: '10px 18px',
                color: 'white',
                fontSize: 13,
                fontWeight: 600,
                width: '100%',
                justifyContent: 'center',
              }}
            />
            {loading && (
              <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                Full mongodump on the server — larger databases may take a while.
              </p>
            )}
          </div>

          {/* Restore */}
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              padding: 24,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg,rgba(245,158,11,.12),rgba(217,119,6,.08))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className="pi pi-cloud-upload" style={{ fontSize: 16, color: '#d97706' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 2 }}>
                  Restore Database
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                  Upload a .tar.gz archive (overwrites current data)
                </div>
              </div>
            </div>
            <input
              ref={restoreFileInputRef}
              type="file"
              accept=".gz,.tar.gz,application/gzip"
              onChange={handleRestoreFileSelected}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                label={
                  restoring
                    ? 'Restoring…'
                    : selectedRestoreFile
                      ? 'Confirm & Restore'
                      : 'Choose Backup File'
                }
                icon={
                  restoring
                    ? 'pi pi-spin pi-spinner'
                    : selectedRestoreFile
                      ? 'pi pi-exclamation-triangle'
                      : 'pi pi-upload'
                }
                onClick={handleRestoreClick}
                loading={restoring}
                severity={selectedRestoreFile ? 'danger' : undefined}
                style={{
                  background: selectedRestoreFile
                    ? undefined
                    : 'linear-gradient(135deg,#3b82f6,#2563eb)',
                  border: selectedRestoreFile ? undefined : 'none',
                  borderRadius: 12,
                  padding: '10px 18px',
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 600,
                  flex: 1,
                  justifyContent: 'center',
                }}
              />
              {selectedRestoreFile && !restoring && (
                <Button
                  label="Cancel"
                  text
                  onClick={() => {
                    setSelectedRestoreFile(null);
                    if (restoreFileInputRef.current) restoreFileInputRef.current.value = '';
                  }}
                  style={{ color: '#6b7280', fontSize: 13, fontWeight: 600 }}
                />
              )}
            </div>
            {selectedRestoreFile && (
              <p style={{ color: '#374151', fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                Selected: <strong>{selectedRestoreFile.name}</strong> ({formatBytes(selectedRestoreFile.size)})
              </p>
            )}
            {restoring && (
              <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                Running mongorestore — drops and repopulates current collections.
              </p>
            )}
          </div>

          {/* Last Backup Status */}
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              padding: 24,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg,rgba(65,105,225,.12),rgba(59,130,246,.08))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className="pi pi-shield" style={{ fontSize: 16, color: '#4169E1' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 2 }}>
                  Last Backup Status
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                  Most recent attempt from any trigger
                </div>
              </div>
            </div>

            {lastStatusLoading ? (
              <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>Loading…</p>
            ) : !lastStatus ? (
              <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>No backups have run yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Tag
                    value={lastStatus.operation === 'restore' ? 'Restore' : 'Backup'}
                    severity={operationSeverity(lastStatus.operation)}
                    style={{ fontSize: 11, fontWeight: 600 }}
                  />
                  <Tag
                    value={lastStatus.status}
                    severity={statusSeverity(lastStatus.status)}
                    style={{ fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}
                  />
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    {lastStatus.trigger_type === 'manual'
                      ? 'Triggered manually'
                      : `Triggered: ${lastStatus.trigger_type}`}
                    {lastStatus.triggered_by_name ? ` by ${lastStatus.triggered_by_name}` : ''}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#1f2937', fontWeight: 600 }}>
                  {formatDate(lastStatus.created_at)}
                </div>
                {lastStatus.status === 'success' ? (
                  <div
                    style={{
                      display: 'flex',
                      gap: 14,
                      fontSize: 12,
                      color: '#6b7280',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontFamily: 'monospace' }}>{lastStatus.archive_file_name || '—'}</span>
                    <span>{formatMB(lastStatus.archive_size_mb)}</span>
                    <span>{formatDuration(lastStatus.duration_seconds)}</span>
                  </div>
                ) : lastStatus.status === 'failed' && lastStatus.error_message ? (
                  <p style={{ fontSize: 12, color: '#dc2626', margin: 0 }}>{lastStatus.error_message}</p>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* ── Backup History Table ──────────────────────────────────────── */}
        <div
          style={{
            background: 'white',
            borderRadius: 20,
            boxShadow: '0 2px 12px rgba(0,0,0,.06)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            marginBottom: 24,
          }}
        >
          {/* Table header bar (matches Students list pattern) */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #f3f4f6',
              background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-history" style={{ fontSize: 14, color: '#4169E1' }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>Backup History</div>
                <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                  {pagination.total_items > 0
                    ? `Showing ${((logsPage - 1) * pageSize) + 1}–${Math.min(logsPage * pageSize, pagination.total_items)} of ${pagination.total_items} attempts`
                    : 'All backup and restore attempts'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={() => fetchBackupLogs(logsPage, pageSize)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: 'white',
                  color: '#4169E1',
                  border: '1.5px solid #c7d2fe',
                  transition: 'all .2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#eff6ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                <i className="pi pi-refresh" style={{ fontSize: 12 }} />
                Refresh
              </button>
              {pagination.total_pages > 0 && (
                <div
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                    border: '1px solid rgba(65,105,225,.15)',
                  }}
                >
                  <span style={{ fontSize: 13, color: '#4169E1', fontWeight: 600 }}>
                    Page {pagination.current_page} of {pagination.total_pages || 1}
                  </span>
                </div>
              )}
            </div>
          </div>

          <Tooltip target=".backup-error-cell" position="top" />

          <DataTable
            value={backupLogs}
            loading={tableLoading}
            dataKey="_id"
            emptyMessage={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '60px 20px',
                  minHeight: 280,
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                    borderRadius: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 20,
                    boxShadow: '0 6px 18px rgba(59,130,246,.2)',
                  }}
                >
                  <i className="pi pi-inbox" style={{ fontSize: 25, color: 'white' }} />
                </div>
                <h3
                  style={{
                    margin: 0,
                    color: '#1e40af',
                    fontSize: 18,
                    fontWeight: 700,
                    marginBottom: 8,
                    textAlign: 'center',
                  }}
                >
                  No Backup Attempts Yet
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: '#64748b',
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: 'center',
                    maxWidth: 360,
                    lineHeight: 1.6,
                  }}
                >
                  Create a backup or run a restore to see history appear here.
                </p>
              </div>
            }
          >
            <Column header="Operation" body={operationTemplate} style={{ width: 110 }} />
            <Column header="Status" body={statusTemplate} style={{ width: 110 }} />
            <Column header="Database" body={databaseTemplate} />
            <Column header="Trigger Type" body={triggerTypeTemplate} style={{ width: 120 }} />
            <Column header="Triggered By" body={triggeredByTemplate} style={{ width: 140 }} />
            <Column header="File" body={fileTemplate} />
            <Column header="Size" body={sizeTemplate} style={{ width: 90 }} />
            <Column header="Duration" body={durationTemplate} style={{ width: 90 }} />
            <Column header="Created" body={createdTemplate} style={{ width: 160 }} />
          </DataTable>

          {pagination.total_pages > 1 && (
            <CustomPaginator
              page={pagination.current_page}
              totalPages={pagination.total_pages}
              totalRecords={pagination.total_items}
              pageSize={pageSize}
              onPageChange={(newPage) => setLogsPage(newPage)}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setLogsPage(1);
              }}
              showInfoBar={false}
            />
          )}
        </div>

        {/* ── Backups This Session ──────────────────────────────────────── */}
        {history.length > 0 && (
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f3f4f6',
                background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-file" style={{ fontSize: 14, color: '#4169E1' }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>
                  Backups This Session
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                  Files downloaded during this browser session
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {history.map((h, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: '#f9fafb',
                    border: '1px solid #f1f5f9',
                    gap: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <i className="pi pi-file" style={{ fontSize: 14, color: '#3b82f6', flexShrink: 0 }} />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#1f2937',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {h.filename}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{h.sizeLabel}</span>
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>{h.createdAt}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── API Logs ─────────────────────────────────────────────────── */}
        {logs.length > 0 && (
          <div
            style={{
              background: 'white',
              borderRadius: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,.06)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f3f4f6',
                background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-list" style={{ fontSize: 14, color: '#4169E1' }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>API Logs</div>
                <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                  Client-side log of backup operations this session
                </div>
              </div>
            </div>
            <div
              style={{
                background: '#f9fafb',
                margin: 16,
                borderRadius: 12,
                padding: 16,
                maxHeight: 360,
                overflowY: 'auto',
                border: '1px solid #f1f5f9',
                fontSize: 13,
                color: '#1f2937',
                fontFamily: 'monospace',
              }}
            >
              {logs.map((log, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: index < logs.length - 1 ? 12 : 0,
                    paddingBottom: index < logs.length - 1 ? 12 : 0,
                    borderBottom: index < logs.length - 1 ? '1px solid #e5e7eb' : 'none',
                  }}
                >
                  <div style={{ marginBottom: 4 }}>
                    <span style={{ color: '#9ca3af', fontSize: 11 }}>[{log.timestamp}]</span>
                    <span style={{ fontWeight: 700, marginLeft: 8, color: '#4169E1', fontSize: 12 }}>
                      {log.action}
                    </span>
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word',
                      color: '#374151',
                      fontSize: 12,
                      lineHeight: 1.5,
                    }}
                  >
                    {log.message}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}